import type { ReactNode } from "react";
import { IconClipboardText } from "@tabler/icons-react";
import { Card, Divider, Group, Heading, Stack, Text, ThemeIcon } from "@/components/ui";
import type { SolicitudDetalle } from "@/lib/types";
import { formatearMonto } from "@/shared/format/money";
import { formatFecha } from "@/shared/format/fecha";

/** Color del badge de estatus fiscal SAT según su vigencia. */
function colorEstatusSat(estatus: string): string {
  const e = estatus.toLowerCase();
  if (e.includes("vigente")) return "exito";
  if (e.includes("rechaz") || e.includes("cancel")) return "peligro";
  if (e.includes("suspend")) return "advertencia";
  return "slate";
}

/**
 * Resumen de la solicitud para que el autorizador tenga el contexto
 * sin tener que abrir el detalle completo. Solo render · nada de fetch.
 */
export function ResumenSolicitudCard({ sol }: { sol: SolicitudDetalle }) {
  return (
    <Card padding="lg">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
          <Group gap="xs">
            <ThemeIcon variant="filled" color="multiva" radius="md" size="md">
              <IconClipboardText size={14} />
            </ThemeIcon>
            <Heading order={5}>Resumen</Heading>
          </Group>
          {sol.cfdi ? (
            <div className="text-right">
              <SubLabel>Total</SubLabel>
              <Text size="xl" fw={700} className="text-[28px] leading-tight">
                {formatearMonto(sol.cfdi.total, sol.cfdi.moneda, { minimumFractionDigits: 2 })}
              </Text>
            </div>
          ) : null}
        </Group>
        <Divider />

        {/* Datos escalares · rejilla pareja de 2 columnas */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Kv
            label="Solicitante"
            value={
              sol.solicitanteNombre ? (
                <Text size="sm">{sol.solicitanteNombre}</Text>
              ) : (
                <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px]">{sol.solicitanteId}</code>
              )
            }
          />
          {sol.organizacion ? (
            <Kv
              label="Organización"
              value={
                <Stack gap={2}>
                  <Text size="sm">{sol.organizacion.nombre}</Text>
                  <Text size="xs" c="dimmed">
                    Sociedad {sol.organizacion.numeroSociedad} · {sol.organizacion.rfc}
                  </Text>
                </Stack>
              }
            />
          ) : null}
          {sol.proveedor ? (
            <Kv
              label="Proveedor"
              value={
                <Stack gap={2}>
                  <Text size="sm">
                    #{sol.proveedor.numeroProveedor} · {sol.proveedor.razonSocial}
                  </Text>
                  <Text size="xs" c="dimmed" ff="mono">
                    {sol.proveedor.rfc}
                  </Text>
                </Stack>
              }
            />
          ) : null}
          {sol.datosGenerales ? (
            <>
              <Kv label="Área" value={<Text size="sm">{sol.datosGenerales.areaNombre}</Text>} />
              <Kv label="Motivo" value={<Text size="sm">{sol.datosGenerales.motivoPago}</Text>} />
              <Kv
                label="Periodo"
                value={
                  <Text size="sm">
                    {formatFecha(sol.datosGenerales.periodoServicioInicio)} →{" "}
                    {formatFecha(sol.datosGenerales.periodoServicioFin)}
                  </Text>
                }
              />
            </>
          ) : null}
        </div>

        {/* CFDI · sección propia, dispuesta horizontalmente */}
        {sol.cfdi ? (
          <>
            <Divider label="CFDI" />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-12">
              <div className="sm:col-span-6">
                <SubLabel>UUID</SubLabel>
                <Group gap={8} wrap="wrap" align="center">
                  <Text size="sm" ff="mono" tt="uppercase" className="break-all">
                    {sol.cfdi.uuid}
                  </Text>
                  {sol.cfdi.statusFiscal ? (
                    <span
                      className="inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-semibold"
                      style={{
                        backgroundColor: `light-dark(var(--color-${colorEstatusSat(sol.cfdi.statusFiscal)}-50), color-mix(in srgb, var(--color-${colorEstatusSat(sol.cfdi.statusFiscal)}-600) 20%, transparent))`,
                        color: `light-dark(var(--color-${colorEstatusSat(sol.cfdi.statusFiscal)}-800), var(--color-${colorEstatusSat(sol.cfdi.statusFiscal)}-200))`,
                      }}
                    >
                      {sol.cfdi.statusFiscal}
                    </span>
                  ) : null}
                </Group>
              </div>
              {sol.cfdi.fechaTimbrado ? (
                <div className="sm:col-span-3">
                  <SubLabel>Fecha de timbrado</SubLabel>
                  <Text size="sm">{formatFecha(sol.cfdi.fechaTimbrado)}</Text>
                </div>
              ) : null}
              <div className="sm:col-span-3">
                <SubLabel>Fecha de emisión</SubLabel>
                <Text size="sm">{formatFecha(sol.cfdi.fechaEmision)}</Text>
              </div>
            </div>
          </>
        ) : null}
      </Stack>
    </Card>
  );
}

/** Etiqueta gris en mayúsculas (mismo aspecto que el label de cada dato del resumen). */
function SubLabel({ children }: { children: ReactNode }) {
  return (
    <Text size="xs" c="dimmed" tt="uppercase" fw={600} className="tracking-wide" style={{ fontSize: 10 }}>
      {children}
    </Text>
  );
}

function Kv({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <SubLabel>{label}</SubLabel>
      <div className="mt-0.5">{value}</div>
    </div>
  );
}
